import { useState, useEffect, useCallback } from "react";

const SLIDES = [
  {
    id: 1,
    badge: "PREMIUM QUALITY",
    headline: "Premium Indian Tea &\nSpices Exporter",
    text: "Delivering authentic Indian agricultural products to global markets with uncompromising quality.",
    cta: "WhatsApp Now",
    ctaLink: "https://wa.me/917584050023?text=Hello%20Pandit%20Export%2C%20I%20am%20interested%20in%20your%20products.",
    bg: "https://public.readdy.ai/ai/img_res/5c832816345b8b66377c3cbcf7a8de33.jpg"
  },
  {
    id: 2,
    badge: "EXPORT STANDARD",
    headline: "Trusted Source for Tea,\nTurmeric & Spices",
    text: "High-quality sourcing with strict quality control, export compliance, and certified standards.",
    cta: "Contact Us",
    ctaAction: "contact",
    bg: "https://readdy.ai/api/search-image?query=Beautiful%20arrangement%20of%20Indian%20spices%20and%20turmeric%20roots%20on%20dark%20wooden%20table%2C%20vibrant%20golden%20turmeric%20powder%20bright%20yellow%20red%20chili%20coriander%20cumin%20seeds%20in%20small%20bowls%2C%20overhead%20shot%20with%20dramatic%20soft%20lighting%2C%20warm%20amber%20golden%20tones%2C%20rich%20and%20luxurious%20aesthetic%2C%20professional%20product%20photography%2C%20dark%20moody%20background%2C%20high%20contrast%20beautiful%20colors&width=1920&height=900&seq=hero002&orientation=landscape"
  },
  {
    id: 3,
    badge: "GLOBAL SUPPLY",
    headline: "Reliable Global\nSupply Partner",
    text: "Competitive pricing, consistent quality, and timely delivery to worldwide destinations.",
    cta: "Get Quote",
    ctaAction: "contact",
    bg: "https://readdy.ai/api/search-image?query=Large%20international%20cargo%20shipping%20port%20at%20sunset%20with%20containers%20being%20loaded%20onto%20cargo%20ship%2C%20aerial%20view%20of%20busy%20export%20terminal%20with%20colorful%20containers%2C%20golden%20hour%20sky%20with%20orange%20pink%20clouds%20reflection%20on%20calm%20water%2C%20industrial%20maritime%20export%20logistics%20background%2C%20epic%20cinematic%20photography%2C%20warm%20sunset%20tones&width=1920&height=900&seq=hero003&orientation=landscape"
  }
];

export default function HeroBanner() {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  const goTo = useCallback((idx: number) => {
    if (animating) return;
    setAnimating(true);
    setTimeout(() => {
      setCurrent(idx);
      setAnimating(false);
    }, 300);
  }, [animating]);

  const next = useCallback(() => {
    goTo((current + 1) % SLIDES.length);
  }, [current, goTo]);

  const prev = useCallback(() => {
    goTo((current - 1 + SLIDES.length) % SLIDES.length);
  }, [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5500);
    return () => clearInterval(timer);
  }, [next]);

  const handleCTA = (slide: typeof SLIDES[0]) => {
    if (slide.ctaLink) {
      window.open(slide.ctaLink, "_blank");
    } else if (slide.ctaAction === "contact") {
      const el = document.getElementById("contact");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const slide = SLIDES[current];

  return (
    <section id="home" className="relative w-full h-[100vh] min-h-[600px] overflow-hidden">
      {/* Background */}
      <div
        className={`absolute inset-0 transition-opacity duration-700 ${animating ? "opacity-0" : "opacity-100"}`}
        style={{
          backgroundImage: `url(${slide.bg})`,
          backgroundSize: "cover",
          backgroundPosition: "center top",
        }}
      />
      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/50"></div>

      {/* Content */}
      <div className={`relative z-10 h-full flex items-center transition-all duration-500 ${animating ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-10 w-full">
          <div className="max-w-2xl">
            {/* Badge */}
            <span className="inline-block bg-[#C9A227] text-white text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-5">
              {slide.badge}
            </span>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-tight mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
              {slide.headline.split("\n").map((line, i) => (
                <span key={i}>
                  {line}
                  {i < slide.headline.split("\n").length - 1 && <br />}
                </span>
              ))}
            </h1>

            {/* Text */}
            <p className="text-lg md:text-xl text-white/85 mb-8 leading-relaxed max-w-lg">
              {slide.text}
            </p>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => handleCTA(slide)}
                className="inline-flex items-center gap-2 bg-[#C9A227] hover:bg-[#b8911e] text-white font-bold px-8 py-4 rounded-full text-base transition-all duration-200 whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              >
                {slide.cta === "WhatsApp Now" && (
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-whatsapp-line"></i>
                  </span>
                )}
                {slide.cta}
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </span>
              </button>
              <a
                href="#about"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("about")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="inline-flex items-center gap-2 border-2 border-white text-white hover:bg-white hover:text-[#1B4332] font-semibold px-8 py-4 rounded-full text-base transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Explore More
              </a>
            </div>

            {/* Stats */}
            <div className="flex gap-8 mt-12">
              {[
                { val: "5+", label: "Products" },
                { val: "25KG+", label: "Min. Order" },
                { val: "Global", label: "Reach" },
              ].map((s) => (
                <div key={s.label} className="text-white">
                  <p className="text-2xl font-bold text-[#C9A227]">{s.val}</p>
                  <p className="text-xs text-white/70 uppercase tracking-wider">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Left/Right Arrows */}
      <button
        onClick={prev}
        className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/40 border border-white/40 rounded-full text-white transition-all cursor-pointer"
      >
        <span className="w-5 h-5 flex items-center justify-center">
          <i className="ri-arrow-left-s-line text-xl"></i>
        </span>
      </button>
      <button
        onClick={next}
        className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/40 border border-white/40 rounded-full text-white transition-all cursor-pointer"
      >
        <span className="w-5 h-5 flex items-center justify-center">
          <i className="ri-arrow-right-s-line text-xl"></i>
        </span>
      </button>

      {/* Dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`transition-all duration-300 rounded-full cursor-pointer ${
              i === current ? "w-8 h-3 bg-[#C9A227]" : "w-3 h-3 bg-white/50 hover:bg-white/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
}
